/**
 * Formats a duration in milliseconds to a human-readable string
 * @param milliseconds - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2m 30s", "1h 15m", "45s")
 */
export function formatDuration(milliseconds: number): string {
  if (milliseconds < 0) return '0s';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    if (remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${hours}h`;
  }
  
  if (minutes > 0) {
    if (remainingSeconds > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${minutes}m`;
  }
  
  return `${remainingSeconds}s`;
}

/**
 * Formats a duration in milliseconds to a precise string with milliseconds
 * @param milliseconds - Duration in milliseconds
 * @returns Formatted duration string (e.g., "2m 30.5s", "1h 15m 30s")
 */
export function formatPreciseDuration(milliseconds: number): string {
  if (milliseconds < 0) return '0ms';
  
  const totalSeconds = milliseconds / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  
  const remainingMinutes = minutes % 60;
  const remainingSeconds = totalSeconds % 60;
  
  if (hours > 0) {
    if (remainingMinutes > 0) {
      return `${hours}h ${remainingMinutes}m ${Math.floor(remainingSeconds)}s`;
    }
    return `${hours}h ${Math.floor(remainingSeconds)}s`;
  }
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
  }
  
  if (totalSeconds >= 1) {
    return `${remainingSeconds.toFixed(1)}s`;
  }
  
  return `${milliseconds}ms`;
}

/**
 * Converts seconds to milliseconds
 * @param seconds - Duration in seconds
 * @returns Duration in milliseconds
 */
export function secondsToMilliseconds(seconds: number): number {
  return seconds * 1000;
}

/**
 * Converts minutes to milliseconds
 * @param minutes - Duration in minutes
 * @returns Duration in milliseconds
 */
export function minutesToMilliseconds(minutes: number): number {
  return minutes * 60 * 1000;
}

/**
 * Converts hours to milliseconds
 * @param hours - Duration in hours
 * @returns Duration in milliseconds
 */
export function hoursToMilliseconds(hours: number): number {
  return hours * 60 * 60 * 1000;
}

/**
 * Calculates reading time estimate based on word count
 * @param wordCount - Number of words
 * @param wordsPerMinute - Average reading speed (default: 200 WPM)
 * @returns Estimated reading time in milliseconds
 */
export function estimateReadingTime(wordCount: number, wordsPerMinute: number = 200): number {
  const minutes = wordCount / wordsPerMinute;
  return minutesToMilliseconds(minutes);
}

/**
 * Formats a timestamp to a readable date and time string
 * @param date - Date object or timestamp
 * @returns Formatted date string
 */
export function formatDateTime(date: Date | number): string {
  const dateObj = typeof date === 'number' ? new Date(date) : date;
  return dateObj.toLocaleString();
}

/**
 * Formats a timestamp to a readable time string
 * @param date - Date object or timestamp
 * @returns Formatted time string
 */
export function formatTime(date: Date | number): string {
  const dateObj = typeof date === 'number' ? new Date(date) : date;
  return dateObj.toLocaleTimeString();
}
